import { TimeEntry } from '../types/TimeEntry';

export interface FixedPriceInvoicingRow {
  rowKey: string;
  customer: string;
  project: string;
  hours: number;
  amount: number;
  rateLabel: string;
}

export const buildFixedPriceInvoicingRowsByPerson = (
  entries: TimeEntry[],
  projectMonthPrices: Record<number, { title: string; monthPrice: number }>,
  activityPrices: Record<string, number>,
  includeInPersonView = true
): Map<string, FixedPriceInvoicingRow[]> => {
  if (!includeInPersonView) return new Map();

  const rowsByPerson = new Map<string, Map<number, FixedPriceInvoicingRow & { rates: Set<number> }>>();

  entries.forEach((entry) => {
    if (entry.Billable === false) return;

    const projectId = entry.Project?.Id;
    if (typeof projectId !== 'number' || (projectMonthPrices[projectId]?.monthPrice || 0) <= 0) return;

    const person = entry.Person?.Title || 'Okänd person';
    const customer = entry.Customer?.Title || 'Okänd kund';
    const activity = entry.Activity?.Title || 'Okänd aktivitet';
    const rate = activityPrices[`${activity}|${customer}`] ?? 0;
    let rows = rowsByPerson.get(person);
    if (!rows) {
      rows = new Map();
      rowsByPerson.set(person, rows);
    }

    const row = rows.get(projectId);
    if (row) {
      row.hours += entry.Hours;
      row.amount += entry.Hours * rate;
      if (rate > 0) row.rates.add(rate);
    } else {
      rows.set(projectId, {
        rowKey: `fixed-price-project-${projectId}`,
        customer,
        project: projectMonthPrices[projectId].title,
        hours: entry.Hours,
        amount: entry.Hours * rate,
        rateLabel: '',
        rates: new Set(rate > 0 ? [rate] : [])
      });
    }
  });

  return new Map(
    Array.from(rowsByPerson.entries()).map(([person, rows]) => [
      person,
      Array.from(rows.values())
        .map(({ rates, ...row }) => {
          const rateValues = Array.from(rates);
          const averageRate = row.hours > 0 ? row.amount / row.hours : 0;
          return {
            ...row,
            rateLabel: rateValues.length === 0
              ? '0 kr'
              : rateValues.length === 1
                ? `${rateValues[0].toFixed(0)} kr`
                : `${averageRate.toFixed(0)} kr (snitt)`
          };
        })
        .sort((first, second) => first.project.localeCompare(second.project, 'sv'))
    ])
  );
};
